import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { company } from '~/company/Company';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  BadgeV3,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useProductSnippetAnalytics } from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import styles from './ProductCardXS.module.scss';

/**
 * ProductCardXS - Extra small size product card
 * Card: mobile 72×92px, web 140×172px
 * Image: mobile 72×64px, web 140×128px
 * Minimal card with only image and name for compact lists
 * Supports optional count badge in top-right corner
 */
const ProductCardXS = observer(
  ({
    product,
    index,
    className,
    productState,
    count,
    disabled = false,
  }: ProductCardVariantProps) => {
    const { handleClickProduct } = useCategoryAnalytics();

    const { currentItem, image } = useProductCardV3(
      product,
      productState?.selectedOffer,
      'xs',
    );

    const { snippetRef, trackSnippetClick } = useProductSnippetAnalytics(
      currentItem,
      index,
    );

    const handleProductClick = useCallback(() => {
      trackSnippetClick();
      handleClickProduct(product);
    }, [product, handleClickProduct, trackSnippetClick]);

    return (
      <div
        ref={snippetRef}
        className={classes([
          styles.card,
          disabled && styles.disabled,
          className,
        ])}
        data-company={company.name}
      >
        {count !== undefined && count > 0 && (
          <BadgeV3
            size="xs"
            variant="outline"
            className={styles.countBadge}
          >{`x${count}`}</BadgeV3>
        )}

        <ProductImage
          src={image}
          alt={currentItem.name}
          size="xs"
          isFavorite={false}
          onToggleFavorite={() => void 0}
          productSlug={product.slug}
          offerSku={currentItem.sku ?? ''}
          onProductClick={handleProductClick}
          className={styles.image}
        />

        <div className={styles.infoWrapper}>
          <ProductInfo
            onProductClick={handleProductClick}
            name={currentItem.name}
            volume=""
            isRatingShown={false}
            size="xs"
            productSlug={product.slug}
            currentSku={currentItem.sku ?? ''}
            className={styles.info}
          />
        </div>

        {disabled && <div className={styles.disabledOverlay} />}
      </div>
    );
  },
);

export default ProductCardXS;
